// 任务设置管理 API
import { createAppRequest } from '@/services/request';
import type { CreateTaskDto, TaskPriority, TaskStatus } from './type';

const request = createAppRequest();

export interface TaskRoleSetting {
  code: string;
  name: string;
  sortWeight: number;
}

export interface TaskNotifySendSettings {
  enabled: boolean;
  reminderEnabled: boolean;
  channel: 'inApp';
  events: {
    taskCreated: boolean;
    assigneeChanged: boolean;
    statusChanged: boolean;
    priorityChanged: boolean;
  };
  priorities: Record<TaskPriority, boolean>;
  dedupeSeconds: number;
}

export interface TaskPreferences {
  syncSchedulesEnabled: boolean;
  removeRecognizedDateText: boolean;
}

// ==================== 任务设置管理 API ====================

// 任务设置相关接口
export const taskSettingsApi = {
  getTaskPreferences: () =>
    request<TaskPreferences>('/bff/tasks/preferences', { method: 'GET' }),

  updateTaskPreferences: (data: Partial<TaskPreferences>) =>
    request<TaskPreferences>('/bff/tasks/preferences', {
      method: 'PATCH',
      data,
    }),

  getTaskRoleSettings: () =>
    request<TaskRoleSetting[]>('/bff/tasks/settings/roles', {
      method: 'GET',
    }),

  updateTaskRoleSettings: (data: { taskRoles: TaskRoleSetting[] }) =>
    request<TaskRoleSetting[]>('/bff/tasks/settings/roles', {
      method: 'PATCH',
      data,
    }),

  getTaskNotifySendSettings: () =>
    request<TaskNotifySendSettings>('/bff/tasks/settings', {
      method: 'GET',
    }),

  updateTaskNotifySendSettings: (data: Partial<TaskNotifySendSettings>) =>
    request<TaskNotifySendSettings>('/bff/tasks/settings', {
      method: 'PATCH',
      data,
    }),

  // 获取任务设置
  getTaskSettings: () =>
    request<{
      defaultPriority: TaskPriority;
      defaultStatus: TaskStatus;
      autoComplete: boolean;
      reminderEnabled: boolean;
      reminderTime: number;
      notificationSettings: {
        email: boolean;
        push: boolean;
        sms: boolean;
      };
    }>('/bff/tasks/settings', {
      method: 'GET',
    }),

  // 更新任务设置
  updateTaskSettings: (data: {
    defaultPriority?: TaskPriority;
    defaultStatus?: TaskStatus;
    autoComplete?: boolean;
    reminderEnabled?: boolean;
    reminderTime?: number;
    notificationSettings?: {
      email?: boolean;
      push?: boolean;
      sms?: boolean;
    };
  }) =>
    request<{ success: boolean }>('/bff/tasks/settings', {
      method: 'PUT',
      data,
    }),

  // 获取任务模板
  getTaskTemplates: () =>
    request<
      Array<{
        id: string;
        name: string;
        description: string;
        template: CreateTaskDto;
        isDefault: boolean;
      }>
    >('/bff/tasks/templates', {
      method: 'GET',
    }),

  // 创建任务模板
  createTaskTemplate: (data: {
    name: string;
    description?: string;
    template: CreateTaskDto;
    isDefault?: boolean;
  }) =>
    request<{ id: string }>('/bff/tasks/templates', {
      method: 'POST',
      data,
    }),

  // 获取任务优先级配置
  getTaskPriorities: () =>
    request<
      Array<{
        value: TaskPriority;
        label: string;
        color: string;
        order: number;
      }>
    >('/bff/tasks/priorities', {
      method: 'GET',
    }),

  // 更新任务优先级配置
  updateTaskPriorities: (
    data: Array<{
      value: TaskPriority;
      label: string;
      color: string;
      order: number;
    }>,
  ) =>
    request<{ success: boolean }>('/bff/tasks/priorities', {
      method: 'PUT',
      data,
    }),
};
